import { usePromotions } from 'components/shared/Promotions/utils/usePromotions'
import { getNumberOfDaysBetweenDates, isDateInRange } from 'helpers/dates'
import { useAppSelector } from 'store/hooks'
import { useApp } from 'store/steps'
import { CityBreakTicket } from 'components/common/CityBreak/types'
import { CITY_BREAK_TICKET_PRICE } from 'constants/prices'

const useCityBreak = () => {
    const { appMode, isCreatorAccommodation } = useApp()
    const { isCityBreakPromoActive } = usePromotions()

    const { tickets, spa, cabanas, accommodation } = useAppSelector(
        (state) => state.cart.items
    )
    const {
        dateFrom,
        dateTo,
        adultCount: adultCountFromCart,
        childCount: childCountFromCart,
    } = accommodation?.detail || {}

    const {
        dates,
        guestsToAssign: {
            adults: adultCountFromState,
            kids: childCountFromState,
        },
    } = useAppSelector((state) => state.accommodation)
    const adultCount = isCreatorAccommodation
        ? adultCountFromCart
        : adultCountFromState
    const childCount = isCreatorAccommodation
        ? childCountFromCart
        : childCountFromState

    const promotionStart = '2025-04-01'
    const promotionEnd = '2025-06-27'

    // TODO: move into helper/s ?
    const adultTickets: CityBreakTicket[] = [
        {
            quantity: adultCount || 0,
            zone: 'jamango',
            person: 'adult',
            ticketType: 'day',
            price: CITY_BREAK_TICKET_PRICE,
        },
    ]
    const childrenTickets: CityBreakTicket[] = childCount
        ? [
              {
                  quantity: childCount,
                  zone: 'jamango',
                  person: 'teen_reduced',
                  ticketType: 'day',
                  price: CITY_BREAK_TICKET_PRICE,
              },
          ]
        : []
    const ticketsOffer = [...adultTickets, ...childrenTickets]

    const datesRangeFromCart = dateFrom && dateTo ? [dateFrom, dateTo] : []
    const datesRangeFromState =
        dates[0] && dates[dates.length - 1]
            ? [dates[0], dates[dates.length - 1]]
            : []
    const datesRange = isCreatorAccommodation
        ? datesRangeFromCart
        : datesRangeFromState

    const daysNumber = isCreatorAccommodation
        ? getNumberOfDaysBetweenDates(dateFrom || '', dateTo || '')
        : dates.length

    const totalPrice =
        CITY_BREAK_TICKET_PRICE *
        ((adultCount || 0) + (childCount || 0)) *
        daysNumber

    // if stay dates for accommodation in cart are within promotion dates range
    const isCityBreakApplicableByAccommodationInCartDates =
        !!dateFrom &&
        isDateInRange(dateFrom, promotionStart, promotionEnd) &&
        !!dateTo &&
        isDateInRange(dateTo, promotionStart, promotionEnd)

    // if stay dates for accommodation in state are within promotion dates range
    const isCityBreakApplicableByAccommodationInStateDates =
        !!dates[0] &&
        isDateInRange(dates[0], promotionStart, promotionEnd) &&
        !!dates[dates.length - 1] &&
        isDateInRange(dates[dates.length - 1], promotionStart, promotionEnd)

    // city break box on tickets step, visible if:
    // - creator is accommodation type
    // - promotion is active (based on end-start dates)
    // - there is accommodation in cart, but for dates only within promotional range (mentioned above)
    const isCityBreakApplicableForTickets =
        isCreatorAccommodation &&
        isCityBreakPromoActive &&
        isCityBreakApplicableByAccommodationInCartDates

    // city break box on accommodation step, visible if:
    // - creator is standard type
    // - promotion is active (based on end-start dates)
    // - there is accommodation in state, but for dates only within promotional range (mentioned above)
    const isCityBreakApplicableForAccommo =
        appMode === 'creator' &&
        isCityBreakPromoActive &&
        isCityBreakApplicableByAccommodationInStateDates

    // city break regulations is visible if:
    // - promotion is active (based on end-start dates)
    // - there is accommodation in cart, but for dates only within promotional range (mentioned above)
    const isCityBreakApplicableForRegulations =
        isCityBreakPromoActive &&
        isCityBreakApplicableByAccommodationInCartDates

    // city break condition for the case with park tickets to deal with:
    // - creator is standard type
    // - in cart, there are tickets to the park
    const isCityBreakDealingWithParkTickets =
        appMode === 'creator' && tickets.length > 0

    // city break condition for the case with upsells needing relax zone:
    // - creator is standard type
    // - in cart, there are spa treatments or cabanas
    const isCityBreakDealingWithLimitedUpsells =
        appMode === 'creator' && (spa.length > 0 || cabanas.length > 0)

    return {
        datesRange,
        daysNumber,
        ticketsOffer,
        totalPrice,
        isCityBreakApplicableForTickets,
        isCityBreakApplicableForAccommo,
        isCityBreakApplicableForRegulations,
        isCityBreakDealingWithParkTickets,
        isCityBreakDealingWithLimitedUpsells,
    }
}

export default useCityBreak
